import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Minus, Plus, Trash2, MessageCircle, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { brl } from "@/lib/format";
import { waLink, buildCartMessage } from "@/lib/whatsapp";
import { trackWhatsAppClick } from "@/lib/analytics";
import { CART } from "@/constants/testIds";

export const CartDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQty, clear, total } = useCart();
  const [customer, setCustomer] = useState({ name: "", phone: "", velorio: "", deliveryAt: "", notes: "" });

  const handleSend = () => {
    trackWhatsAppClick("cart_checkout", { items_count: items.length, price: total });
    const msg = buildCartMessage({ items, customer });
    window.open(waLink(msg), "_blank");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        data-testid={CART.drawer}
        side="right"
        className="w-full sm:max-w-md p-0 bg-white border-l border-[#E5E3DB] flex flex-col"
      >
        <SheetHeader className="px-6 py-5 border-b border-[#E5E3DB] text-left">
          <SheetTitle className="font-serif text-2xl text-[#2D4A3E]">Seu pedido</SheetTitle>
          <SheetDescription className="text-xs text-[#5C5C5C]">
            Revise os itens, complete os dados de entrega e envie pelo WhatsApp.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {items.length === 0 ? (
            <div className="py-16 text-center">
              <ShoppingBag className="h-10 w-10 text-[#BFA573] mx-auto" strokeWidth={1.2} />
              <p className="mt-4 font-serif text-xl text-[#2D4A3E]">Nenhum item ainda</p>
              <p className="mt-2 text-sm text-[#5C5C5C]">Adicione coroas do catálogo para montar seu pedido.</p>
            </div>
          ) : (
            <>
              <ul className="space-y-4">
                {items.map((it, idx) => (
                  <li
                    key={it.id}
                    data-testid={CART.item(idx)}
                    className="flex gap-3 border border-[#E5E3DB] rounded-xl p-3"
                  >
                    <img src={it.product.img} alt="" className="h-20 w-20 object-cover rounded-lg flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-serif text-lg text-[#2D4A3E] leading-tight truncate">{it.product.name}</p>
                          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BFA573]">
                            {it.product.ref}{it.size ? ` · ${it.size.label} ${it.size.dim}` : ""}
                          </p>
                        </div>
                        <button
                          onClick={() => removeItem(it.id)}
                          data-testid={CART.remove(idx)}
                          aria-label="Remover"
                          className="text-[#5C5C5C] hover:text-[#1F352B]"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      {it.faixa && (
                        <p className="mt-1 text-xs text-[#5C5C5C] italic truncate">Faixa: "{it.faixa}"</p>
                      )}
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQty(it.id, it.quantity - 1)}
                            data-testid={CART.dec(idx)}
                            className="h-7 w-7 rounded-full border border-[#E5E3DB] flex items-center justify-center hover:border-[#2D4A3E]">
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="text-sm w-5 text-center">{it.quantity}</span>
                          <button
                            onClick={() => updateQty(it.id, it.quantity + 1)}
                            data-testid={CART.inc(idx)}
                            className="h-7 w-7 rounded-full border border-[#E5E3DB] flex items-center justify-center hover:border-[#2D4A3E]">
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <p className="text-sm font-semibold text-[#1C1C1C] tabular-nums">
                          {it.price != null ? brl(it.price * it.quantity) : "Sob consulta"}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="pt-2 space-y-3">
                <h3 className="font-serif text-lg text-[#2D4A3E]">Dados de entrega</h3>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label htmlFor="cname" className="text-xs text-[#5C5C5C]">Seu nome</Label>
                    <Input id="cname" data-testid={CART.customerName} value={customer.name}
                           onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                           className="mt-1 border-[#E5E3DB] focus-visible:ring-[#2D4A3E]" />
                  </div>
                  <div>
                    <Label htmlFor="cphone" className="text-xs text-[#5C5C5C]">Telefone</Label>
                    <Input id="cphone" data-testid={CART.customerPhone} value={customer.phone}
                           onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                           placeholder="(21) 9 0000-0000"
                           className="mt-1 border-[#E5E3DB] focus-visible:ring-[#2D4A3E]" />
                  </div>
                  <div>
                    <Label htmlFor="cvel" className="text-xs text-[#5C5C5C]">Velório / endereço de entrega</Label>
                    <Input id="cvel" data-testid={CART.velorio} value={customer.velorio}
                           onChange={(e) => setCustomer({ ...customer, velorio: e.target.value })}
                           placeholder="Ex: Capela 3, Cemitério do Caju"
                           className="mt-1 border-[#E5E3DB] focus-visible:ring-[#2D4A3E]" />
                  </div>
                  <div>
                    <Label htmlFor="cdate" className="text-xs text-[#5C5C5C]">Data e hora desejadas</Label>
                    <Input id="cdate" data-testid={CART.deliveryDate} value={customer.deliveryAt}
                           onChange={(e) => setCustomer({ ...customer, deliveryAt: e.target.value })}
                           placeholder="Ex: hoje às 16h"
                           className="mt-1 border-[#E5E3DB] focus-visible:ring-[#2D4A3E]" />
                  </div>
                  <div>
                    <Label htmlFor="cnotes" className="text-xs text-[#5C5C5C]">Observações (opcional)</Label>
                    <Textarea id="cnotes" data-testid={CART.notes} value={customer.notes}
                              onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
                              rows={2}
                              className="mt-1 border-[#E5E3DB] focus-visible:ring-[#2D4A3E] resize-none" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#E5E3DB] px-6 py-5 bg-[#F9F8F6]">
            <div className="flex items-baseline justify-between mb-4">
              <span className="text-sm text-[#5C5C5C]">Total estimado</span>
              <span className="font-serif font-semibold text-[30px] leading-none text-[#2D4A3E] tabular-nums tracking-tight">{brl(total)}</span>
            </div>
            <button
              onClick={handleSend}
              data-testid={CART.sendBtn}
              className="btn-whatsapp w-full"
            >
              <MessageCircle className="h-4 w-4" /> Enviar pedido pelo WhatsApp
            </button>
            <button
              onClick={clear}
              data-testid={CART.clearBtn}
              className="mt-2 w-full text-xs text-[#5C5C5C] hover:text-[#1F352B] py-2"
            >
              Limpar pedido
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
